import { Tooltip } from 'antd';
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useContentFramingStore } from '../../../state/contentFramingStore';
import { getToken } from '@/utils/localstorage';

 type CopilotProps = {
  tooltip?: string;
  onClose?: () => void;
};

const Copilot: React.FC<CopilotProps> = ({ tooltip = 'Co-Pilot', onClose }) => {
  const copilotState = useContentFramingStore((s) => s.copilot);

  const copilot = useContentFramingStore((s) => s.copilot);
  const setCopilotMode = useContentFramingStore((s) => s.setCopilotMode);
  const openCopilot = useContentFramingStore((s) => s.openCopilot);

  const closeCopilotStore = useContentFramingStore((s) => s.closeCopilot);
  const [prompt, setPrompt] = useState('What should Alpha Agent change or add?');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { setRowInputText, getRowInputText, getRowContext } = useContentFramingStore();
  const historyPush = useContentFramingStore((s) => s.historyPush);
  const historyBack = useContentFramingStore((s) => s.historyBack);
  const historyForward = useContentFramingStore((s) => s.historyForward);
  

  const closeAll = useCallback(() => {
    setCopilotMode('IconButton');
    closeCopilotStore();
    onClose?.();
  }, [onClose]);


  // Common submit routine
  const submitPrompt = useCallback(async (action: string) => {
    try {
      setCopilotMode('Loading');
      const existing = getRowInputText(copilotState.rowId || '') || '';
      historyPush(existing);
      const token = await getToken();
      const { data } = await axios.post(`${process.env.API_URL}/copilot/edit`, {
        rowId: copilotState.rowId || '',
        action: action,
        instruction: prompt,
        text: existing || getRowContext(copilotState.rowId || '') || ''  ,
        context: getRowContext(copilotState.rowId || ''),
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const updated = data?.updatedText || '';
      if (updated) {
        historyPush(updated);
        setRowInputText(copilotState.rowId || '', updated);
      }
    } catch (_) {

    } finally {
        setCopilotMode('ControlBox');
        setPrompt('What should Alpha Agent change?');
    }
  }, [getRowInputText, copilotState.rowId, prompt, setCopilotMode, setRowInputText]);

return (
    <div
      style={{ position: 'fixed', top: copilotState.y, left: copilotState.x + 26, zIndex: 9999 }}
      className=""
      data-row-id={copilotState.rowId || ''}
    >
      {copilot.mode === 'IconButton' && (
        <div
          className="flex items-center gap-1 bg-white/90 border border-gray-300 rounded-md shadow px-1.5 py-1 cursor-pointer"
          onClick={(e) => { e.stopPropagation();  openCopilot(copilotState.rowId || '', copilotState.x, copilotState.y); setCopilotMode('AlphaCo-pilot'); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Tooltip title={tooltip}>
            <img src="assets/copilot_icons/Co-Pilot Edit Icon.jpg" alt="Co-Pilot" className="w-5 h-5 object-contain cursor-pointer" />
          </Tooltip>
        </div>
      )}
  
      {copilot.mode === 'AlphaCo-pilot' && (
        <div
          className="min-w-[320px] max-w-[380px] bg-white border border-gray-300 rounded-xl shadow-lg px-3 py-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            placeholder={"What should Alpha Agent change or add?"}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitPrompt('Auto-rewrite');
              }
            }}
            className="w-full text-[13px] italic text-gray-800 border-b border-red-400 pb-1 mb-2 outline-none"
            maxLength={250}
          />
          <ul className="text-[13px] text-gray-800 space-y-1">
            <li className="flex items-center gap-2 cursor-pointer" onClick={() => submitPrompt('Auto-rewrite')}><img src="assets/copilot_icons/Auto-rewrite icon.jpg" alt="Auto-rewrite" className="w-4 h-4" /> Auto-rewrite</li>
            <li className="flex items-center gap-2 cursor-pointer" onClick={() => submitPrompt('Condense')}><img src="assets/copilot_icons/Condense Icon.png" alt="Condense" className="w-4 h-4" /> Condense</li>
            <li className="flex items-center gap-2 cursor-pointer" onClick={() => submitPrompt('Expand')}><img src="assets/copilot_icons/Expand Icon.png" alt="Expand" className="w-4 h-4" /> Expand</li>
            <li className="flex items-center gap-2 cursor-pointer" onClick={() => submitPrompt('More critical')}><img src="assets/copilot_icons/More Critical icon.png" alt="More Critical" className="w-4 h-4" /> More critical</li>
            <li className="flex items-center gap-2 cursor-pointer" onClick={() => submitPrompt('More positive')}><img src="assets/copilot_icons/More Positive Icon.png" alt="More Positive" className="w-4 h-4" /> More positive</li>
          </ul>
        </div>
      )}
  
      {copilot.mode === 'Loading' && (
        <div
          className="min-w-[320px] max-w-[380px] bg-white border border-gray-300 rounded-xl shadow-lg px-3 py-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-[13px] italic text-gray-700">Waiting for response…</div>
            <button className="text-xs px-2 py-1 border rounded hover:bg-gray-50" onClick={() => closeAll()}>Exit</button>
          </div>
        </div>
      )}
  
      {copilot.mode === 'ControlBox' && (
        <div
          className="min-w-[420px] max-w-[480px] bg-white border border-gray-300 rounded-xl shadow-lg px-3 py-2"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          
          <div className="flex items-center justify-between mb-2">
            <button className="text-sm text-gray-700" onClick={() => setCopilotMode('AlphaCo-pilot')}>&lt; Back</button>
            <button
              className="text-xs px-2 py-1 rounded bg-green-700 text-white hover:brightness-110"
              onClick={() => { closeAll(); }}
            >✓ Keep it</button>
          </div>
          <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <button title="Regenerate" className="p-1 border rounded hover:bg-gray-50 cursor-pointer" onClick={() => submitPrompt('Auto-rewrite')}>
              <img src="assets/copilot_icons/Refresh Icon.png" alt="Regenerate" className="w-6 h-5" />
            </button>
            <button title="Back" className="p-1 border rounded hover:bg-gray-50 cursor-pointer" onClick={() => {
              const prev = historyBack();
              if (prev) setRowInputText(copilotState.rowId || '', prev);
            }}>
              <img src="assets/copilot_icons/Back Icon.png" alt="Back" className="w-8 h-5" />
            </button>
            <button title="Forward" className="p-1 border rounded hover:bg-gray-50 cursor-pointer" onClick={() => {
              const next = historyForward();
              if (next) setRowInputText(copilotState.rowId || '', next);
            }}>
              <img src="assets/copilot_icons/Back Icon.png" alt="Forward" className="w-8 h-5 rotate-180" />
            </button>
          </div>
          <div className="border rounded px-2 text-[13px] whitespace-pre-wrap max-h-32 overflow-auto w-full">         
          <input
            ref={inputRef}
            placeholder={"What should Alpha Agent change?"}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitPrompt('auto-rewrite');
              }
            }}
            className="w-full text-[13px] italic text-gray-800 border-b border-red-400 pb-1 outline-none"
            maxLength={250}
          />
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default Copilot;


